/**
 * Installation Checklist Dialog - For technicians to track splice completion
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Pole, Cable } from '../../types';
import { traceOpticalPath } from '../../utils/opticalPathTracer';
import { indexZakaznickychVlakien, zakaznikNaVlakne,
         indexPortovSplitterov, portNaVlakne } from '../../utils/zakaznickeVlakna';
import { usePoleStore, useCableStore } from '../../stores';
import { InstallationTechDialog } from './InstallationTechDialog';
import './InstallationChecklistDialog.css';

interface InstallationChecklistDialogProps {
  pole: Pole;
  onClose: () => void;
}

interface CablePreparation {
  id: string;
  cable: Cable;
  fibers: number[]; // List of fiber numbers to prepare
  fiberColors: string[]; // List of fiber color names
  completed: boolean;
}

interface SpliceTask {
  id: string;
  type: 'splice' | 'splitter_input' | 'splitter_output' | 'custom' | 'drop';
  index: number;
  description: string;
  fromCable?: Cable;
  fromFiber?: number;
  toCable?: Cable;
  toFiber?: number;
  splitterType?: string;
  splitterPort?: number;
  signalBeforeSplitter?: number | null;
  signalAfterSplitter?: number | null;
  signalAtEnd?: number | null;
  completed: boolean;
  /** Prípojka: port zákazníckeho splittra na druhom konci vlákna */
  dropTarget?: {
    poleId: number;
    poleName: string;
    splitterIndex: number;
    outputNumber: number;
    splitterTyp: string | null;
    zakaznik: string | null;
    popis: string;
  };
  note?: string; // For custom tasks
}

interface CableGroup {
  id: string;
  fromCable?: Cable;
  toCable?: Cable;
  fromCableName: string;
  toCableName: string;
  splices: SpliceTask[];
  allCompleted: boolean;
}

interface SplitterGroup {
  id: string;
  splitterType: string;
  inputTask: SpliceTask;
  outputTasks: SpliceTask[];
  allCompleted: boolean;
}

export const InstallationChecklistDialog: React.FC<InstallationChecklistDialogProps> = ({
  pole,
  onClose,
}) => {
  const { poles, updatePole, fetchPoles } = usePoleStore();
  const { cables } = useCableStore();
  const [cablePreparations, setCablePreparations] = useState<CablePreparation[]>([]);
  const [tasks, setTasks] = useState<SpliceTask[]>([]);
  const [currentStep, setCurrentStep] = useState<'preparation' | 'splicing'>('preparation');
  const [saving, setSaving] = useState(false);
  const [touchStart, setTouchStart] = useState<{ x: number; taskId: string } | null>(null);
  const [swipeOffset, setSwipeOffset] = useState<Record<string, number>>({});

  // Custom task dialog state
  const [showCustomTaskDialog, setShowCustomTaskDialog] = useState(false);
  const [customTaskName, setCustomTaskName] = useState('');
  const [customTaskNote, setCustomTaskNote] = useState('');
  const [editingCustomTask, setEditingCustomTask] = useState<string | null>(null);

  const getSplitterLoss = (type: string): number => {
    const losses: Record<string, number> = {
      '1:2': 3.5, '1:4': 7.0, '1:8': 10.5, '1:16': 13.5, '1:32': 17.0, '1:64': 20.5,
    };
    return losses[type] || 0;
  };

  const getSplitterPortCount = (type: string): number => {
    const match = type.match(/1:(\d+)/);
    return match ? parseInt(match[1]) : 4;
  };

  const colors = ['modrá', 'oranžová', 'zelená', 'hnedá', 'šedá', 'biela', 'červená', 'čierna', 'žltá', 'fialová', 'ružová', 'tyrkysová'];

  // Fiber color mapping with background colors
  const fiberColorStyles: { [key: string]: { bg: string; text: string } } = {
    'modrá': { bg: '#E3F2FD', text: '#0D47A1' },      // Light blue bg, dark blue text
    'oranžová': { bg: '#FFF3E0', text: '#E65100' },   // Light orange bg, dark orange text
    'zelená': { bg: '#E8F5E9', text: '#1B5E20' },     // Light green bg, dark green text
    'hnedá': { bg: '#EFEBE9', text: '#4E342E' },      // Light brown bg, dark brown text
    'šedá': { bg: '#F5F5F5', text: '#424242' },       // Light grey bg, dark grey text
    'biela': { bg: '#FAFAFA', text: '#212121' },      // Very light grey bg, black text
    'červená': { bg: '#FFEBEE', text: '#B71C1C' },    // Light red bg, dark red text
    'čierna': { bg: '#EEEEEE', text: '#000000' },     // Light grey bg, black text
    'žltá': { bg: '#FFFDE7', text: '#F57F17' },       // Light yellow bg, dark yellow text
    'fialová': { bg: '#F3E5F5', text: '#4A148C' },    // Light purple bg, dark purple text
    'ružová': { bg: '#FCE4EC', text: '#880E4F' },     // Light pink bg, dark pink text
    'tyrkysová': { bg: '#E0F7FA', text: '#006064' },  // Light cyan bg, dark cyan text
  };

  // Cable group colors (for border)
  const cableGroupColors = [
    '#2196F3', // Blue
    '#4CAF50', // Green
    '#FF9800', // Orange
    '#9C27B0', // Purple
    '#F44336', // Red
    '#00BCD4', // Cyan
    '#FF5722', // Deep Orange
    '#3F51B5', // Indigo
  ];

  const getFiberColorName = (fiberNumber: number): string => {
    return colors[(fiberNumber - 1) % colors.length];
  };

  const getFiberColorStyle = (fiberNumber: number): { bg: string; text: string } => {
    const colorName = getFiberColorName(fiberNumber);
    return fiberColorStyles[colorName] || { bg: '#FFFFFF', text: '#000000' };
  };

  const getCableName = (cable: Cable | undefined): string => {
    if (!cable) return '';
    const fromPole = poles.find(p => p.id === cable.from_pole_id);
    const toPole = poles.find(p => p.id === cable.to_pole_id);
    return `${fromPole?.name || `#${cable.from_pole_id}`} → ${toPole?.name || `#${cable.to_pole_id}`}`;
  };

  // Get consistent cable color based on cable ID
  const getCableGroupColor = (cableId: number | undefined): string => {
    if (!cableId) return cableGroupColors[0];
    // Use cable ID to get consistent color
    return cableGroupColors[cableId % cableGroupColors.length];
  };

  useEffect(() => {
    // Find the current pole in poles array to get latest data
    const currentPole = poles.find(p => p.id === pole.id) || pole;

    console.log('[InstallationChecklist] useEffect triggered');
    console.log('[InstallationChecklist] pole.id:', pole.id);
    console.log('[InstallationChecklist] poles.length:', poles.length);
    console.log('[InstallationChecklist] currentPole === pole:', currentPole === pole);
    console.log('[InstallationChecklist] currentPole.custom_tasks:', (currentPole as any).custom_tasks);

    const generatedTasks: SpliceTask[] = [];
    const generatedPreparations: CablePreparation[] = [];
    const cableMap = new Map<number, { cable: Cable; fibers: Set<number> }>();
    const cassette = currentPole.cassette;
    const checklist = currentPole.installation_checklist || {};

    // Load custom tasks FIRST (they should work even without cassette)
    const customTasks = (currentPole as any).custom_tasks || {};
    console.log('[InstallationChecklist] Loading custom tasks:', customTasks);
    Object.keys(customTasks).forEach(taskId => {
      const customTask = customTasks[taskId];
      console.log('[InstallationChecklist] Adding custom task:', taskId, customTask);
      generatedTasks.push({
        id: taskId,
        type: 'custom',
        index: -1,
        description: customTask.description,
        note: customTask.note,
        completed: customTask.completed,
      });
    });

    if (!cassette) {
      console.log('[InstallationChecklist] No cassette, only custom tasks');
      setTasks(generatedTasks);
      setCablePreparations([]);
      return;
    }

    // Kto visí na ktorom vlákne. Zákazník môže byť na splittri tu a fyzicky
    // pripojený na inom stĺpiku (alebo naopak) — technik pri zváraní potrebuje
    // vedieť, čie vlákno práve zvára.
    const vlaknaZakaznikov = indexZakaznickychVlakien(poles);
    const komuPatri = (cableId?: number | null, fiber?: number | null): string => {
      const z = zakaznikNaVlakne(vlaknaZakaznikov, cableId, fiber);
      return z ? `  👤 ${z.meno}${z.zoStlpika ? ` (zo ${z.zoStlpika})` : ''}` : '';
    };

    // Prípojky k zákazníkom — koniec vlákna idúceho k domu. Splitter, ktorý ho
    // kŕmi, môže byť na inom stĺpiku; technik tu má vidieť, či je port voľný
    // a koho tam prípadne pripojiť.
    // Prípojka môže byť zapísaná v hlavnej kazete aj v zákazníckej — je to
    // fyzicky jeden stĺpik a technik má checklist otvorený nad ním, nie nad
    // virtuálnym dvojčaťom. Berieme teda obe kazety.
    const zakKazeta = poles.find(
      (p) => p.parent_pole_id === currentPole.id && p.is_virtual,
    );
    const zdrojePripojok: { drops: any[]; prefix: string; kde: string }[] = [
      { drops: ((cassette as any).drops || []) as any[], prefix: 'drop', kde: '' },
      {
        drops: ((zakKazeta?.cassette as any)?.drops || []) as any[],
        prefix: 'drop_zak',
        kde: ' [zákaznícka kazeta]',
      },
    ];

    const portyNaVlaknach = indexPortovSplitterov(poles);
    zdrojePripojok.forEach(({ drops, prefix, kde }) => {
      drops.forEach((drop, index) => {
        const taskId = `${prefix}_${index}`;
        const kabel = cables.find((c) => c.id === drop.cable_id);
        const port = portNaVlakne(portyNaVlaknach, drop.cable_id, drop.fiber);
        const farba = getFiberColorName(drop.fiber || 0);

        let popis = `Prípojka k zákazníkovi${kde} — ${getCableName(kabel)} vlákno ${drop.fiber} (${farba})`;
        if (drop.label) popis += `, ${drop.label}`;
        if (port) {
          popis += port.zakaznik
            ? `  👤 ${port.zakaznik} (port ${port.vystup} splittra na ${port.poleName})`
            : `  ⬜ VOĽNÝ port ${port.vystup} splittra ${port.splitterTyp} na ${port.poleName}`;
        } else {
          popis += '  ⚠️ na tomto vlákne nie je zákaznícky splitter';
        }

        let signalTu: number | null = null;
        const cesta = traceOpticalPath(drop.cable_id, drop.fiber, poles, cables);
        if (cesta.found && cesta.isValid) signalTu = cesta.finalPower;

        generatedTasks.push({
          id: taskId,
          type: 'drop',
          index,
          description: popis,
          toCable: kabel,
          toFiber: drop.fiber,
          signalAtEnd: signalTu,
          completed: checklist[taskId] || false,
          dropTarget: port
            ? {
                poleId: port.poleId,
                poleName: port.poleName,
                splitterIndex: port.splitterIndex,
                outputNumber: port.vystup,
                splitterTyp: port.splitterTyp,
                zakaznik: port.zakaznik,
                popis: `${getCableName(kabel)} vlákno ${drop.fiber} (${farba})`
                  + (drop.label ? `, ${drop.label}` : ''),
              }
            : undefined,
        });
      });
    });

    if (cassette.splices) {
      cassette.splices.forEach((splice, index) => {
        const taskId = `splice_${index}`;
        const fromCable = splice.from_source_type === 'cable'
          ? cables.find(c => c.id === splice.from_cable_id)
          : undefined;
        const toCable = cables.find(c => c.id === splice.to_cable_id);

        // Track cables and fibers for preparation
        if (fromCable && splice.from_fiber) {
          if (!cableMap.has(fromCable.id)) {
            cableMap.set(fromCable.id, { cable: fromCable, fibers: new Set() });
          }
          cableMap.get(fromCable.id)!.fibers.add(splice.from_fiber);
        }
        if (toCable && splice.to_fiber) {
          if (!cableMap.has(toCable.id)) {
            cableMap.set(toCable.id, { cable: toCable, fibers: new Set() });
          }
          cableMap.get(toCable.id)!.fibers.add(splice.to_fiber);
        }

        let signalAtEnd: number | null = null;
        if (toCable && splice.to_fiber) {
          const pathResult = traceOpticalPath(splice.to_cable_id, splice.to_fiber, poles, cables);
          if (pathResult.found) {
            signalAtEnd = pathResult.finalPower;
          }
        }

        const fromFiberColor = getFiberColorName(splice.from_fiber || 0);
        const toFiberColor = getFiberColorName(splice.to_fiber || 0);
        const fromCableName = getCableName(fromCable);
        const toCableName = getCableName(toCable);

        generatedTasks.push({
          id: taskId,
          type: 'splice',
          index,
          description: (splice.from_source_type === 'olt_port'
            ? `OLT Port ${splice.from_olt_port} → ${toCableName} vlákno ${splice.to_fiber} (${toFiberColor})`
            : `${fromCableName} vlákno ${splice.from_fiber} (${fromFiberColor}) → ${toCableName} vlákno ${splice.to_fiber} (${toFiberColor})`)
            + (komuPatri(splice.to_cable_id, splice.to_fiber)
               || komuPatri(splice.from_cable_id, splice.from_fiber)),
          fromCable,
          fromFiber: splice.from_fiber,
          toCable,
          toFiber: splice.to_fiber,
          signalAtEnd,
          completed: checklist[taskId] || false,
        });
      });
    }

    if (cassette.splitters) {
      cassette.splitters.forEach((splitter, index) => {
        const taskId = `splitter_${index}_input`;
        let signalBeforeSplitter: number | null = null;
        let signalAfterSplitter: number | null = null;

        if (splitter.input_source_type === 'cable' && splitter.input_cable_id && splitter.input_fiber) {
          const pathResult = traceOpticalPath(splitter.input_cable_id, splitter.input_fiber, poles, cables);
          if (pathResult.found) {
            signalBeforeSplitter = pathResult.finalPower;
            const splitterLoss = getSplitterLoss(splitter.type);
            signalAfterSplitter = signalBeforeSplitter - splitterLoss;
          }
        }

        const inputCable = splitter.input_source_type === 'cable'
          ? cables.find(c => c.id === splitter.input_cable_id)
          : undefined;

        // Track cables and fibers for preparation
        if (inputCable && splitter.input_fiber) {
          if (!cableMap.has(inputCable.id)) {
            cableMap.set(inputCable.id, { cable: inputCable, fibers: new Set() });
          }
          cableMap.get(inputCable.id)!.fibers.add(splitter.input_fiber);
        }

        const inputFiberColor = getFiberColorName(splitter.input_fiber || 0);
        const inputCableName = getCableName(inputCable);

        // Toto JE ten zvar na pigtail splittra — v zozname zvarov sa už
        // neopakuje, tak nech je z textu jasné, že je to práca, nie
        // nastavenie. (Dohodnuté 1. 10. 2026: zvary ostávajú vlákno↔vlákno.)
        let inputDescription = '';
        if (splitter.input_source_type === 'olt_port') {
          inputDescription = `Zvariť OLT port ${splitter.input_olt_port} na vstup splittra č.${index + 1} (${splitter.type})`;
        } else if (splitter.input_source_type === 'splitter_output') {
          // input_splitter_output is 0-based index, so add 1 to get port number
          const portNumber = (splitter.input_splitter_output !== undefined) ? splitter.input_splitter_output + 1 : '?';
          inputDescription = `Zvariť výstup ${portNumber} splittra č.${(splitter.input_splitter_index || 0) + 1} na vstup splittra č.${index + 1} (${splitter.type})`;
        } else {
          inputDescription = `Zvariť ${inputCableName} vlákno ${splitter.input_fiber} (${inputFiberColor}) na vstup splittra č.${index + 1} (${splitter.type})`;
        }

        generatedTasks.push({
          id: taskId,
          type: 'splitter_input',
          index,
          description: inputDescription,
          fromCable: inputCable,
          fromFiber: splitter.input_fiber,
          splitterType: splitter.type,
          signalBeforeSplitter,
          signalAfterSplitter,
          completed: checklist[taskId] || false,
        });

        // Generate tasks for ALL ports (not just connected ones)
        const portCount = getSplitterPortCount(splitter.type);
        for (let portIndex = 0; portIndex < portCount; portIndex++) {
          const taskId = `splitter_${index}_output_${portIndex}`;
          const output = splitter.outputs?.[portIndex];

          let outputCable: Cable | undefined;
          let outputFiber: number | undefined;
          let signalAtEnd: number | null = null;
          let isConnectedToSplitter = false;
          let targetSplitterIndex: number | undefined;

          if (output?.cable_id && output.fiber) {
            outputCable = cables.find(c => c.id === output.cable_id);
            outputFiber = output.fiber;

            // Track cables and fibers for preparation
            if (outputCable && outputFiber) {
              if (!cableMap.has(outputCable.id)) {
                cableMap.set(outputCable.id, { cable: outputCable, fibers: new Set() });
              }
              cableMap.get(outputCable.id)!.fibers.add(outputFiber);
            }

            // Calculate signal
            const pathResult = traceOpticalPath(output.cable_id, outputFiber, poles, cables);
            if (pathResult.found) {
              signalAtEnd = pathResult.finalPower;
            }

            // Check if this output connects to another splitter
            if (cassette && cassette.splitters) {
              cassette.splitters.forEach((otherSplitter, otherIndex) => {
                if (otherIndex !== index &&
                    otherSplitter.input_source_type === 'splitter_output' &&
                    otherSplitter.input_splitter_index === index &&
                    otherSplitter.input_splitter_output === portIndex) {
                  isConnectedToSplitter = true;
                  targetSplitterIndex = otherIndex;
                }
              });
            }
          }

          const outputFiberColor = outputFiber ? getFiberColorName(outputFiber) : '';
          const outputCableName = outputCable ? getCableName(outputCable) : '';

          let description = `Splitter ${splitter.type} port ${portIndex + 1}`;
          if (isConnectedToSplitter && targetSplitterIndex !== undefined) {
            description += ` → Splitter #${targetSplitterIndex + 1}`;
          } else if (outputCable && outputFiber) {
            description += ` → ${outputCableName} vlákno ${outputFiber} (${outputFiberColor})`;
          } else {
            description += ` → Nepripojené`;
          }

          // Meno zákazníka: najprv to, čo je zapísané priamo na výstupe,
          // inak podľa vlákna (zákazník môže byť pripojený na inom stĺpiku).
          const naVystupe = (splitter.outputs?.[portIndex] as any)?.customer_name;
          if (naVystupe) {
            description += `  👤 ${String(naVystupe).trim()}`;
          } else {
            description += komuPatri(outputCable?.id, outputFiber);
          }

          generatedTasks.push({
            id: taskId,
            type: 'splitter_output',
            index,
            description,
            toCable: outputCable,
            toFiber: outputFiber,
            splitterType: splitter.type,
            splitterPort: portIndex + 1,
            signalAtEnd,
            completed: checklist[taskId] || false,
          });
        }
      });
    }

    // Generate cable preparations from cableMap
    cableMap.forEach((value, cableId) => {
      const fiberArray = Array.from(value.fibers).sort((a, b) => a - b);
      const fiberColors = fiberArray.map(f => getFiberColorName(f));
      generatedPreparations.push({
        id: `cable_prep_${cableId}`,
        cable: value.cable,
        fibers: fiberArray,
        fiberColors,
        completed: checklist[`cable_prep_${cableId}`] || false,
      });
    });

    console.log('[InstallationChecklist] Total tasks generated:', generatedTasks.length);
    console.log('[InstallationChecklist] Custom tasks count:', Object.keys(customTasks).length);

    setTasks(generatedTasks);
    setCablePreparations(generatedPreparations);
  }, [pole.id, poles, cables]); // Use pole.id instead of pole to trigger on pole updates

  const toggleCablePreparation = async (prepId: string) => {
    const newPreps = cablePreparations.map(p => p.id === prepId ? { ...p, completed: !p.completed } : p);
    setCablePreparations(newPreps);
    await savePreparations(newPreps);
  };

  const toggleTask = async (taskId: string) => {
    const newTasks = tasks.map(t => t.id === taskId ? { ...t, completed: !t.completed } : t);
    setTasks(newTasks);
    await saveTasks(newTasks);
  };

  const addCustomTask = async () => {
    if (!customTaskName.trim()) return;

    const taskId = `custom_${Date.now()}`;
    const newTask: SpliceTask = {
      id: taskId,
      type: 'custom',
      index: -1,
      description: customTaskName.trim(),
      note: customTaskNote.trim() || undefined,
      completed: false,
    };

    // Add to local state immediately for instant feedback
    const newTasks = [...tasks, newTask];
    setTasks(newTasks);

    // Save to backend
    await saveTasks(newTasks);

    // Reset dialog
    setCustomTaskName('');
    setCustomTaskNote('');
    setShowCustomTaskDialog(false);
  };

  const editCustomTask = async (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task || task.type !== 'custom') return;

    setCustomTaskName(task.description);
    setCustomTaskNote(task.note || '');
    setEditingCustomTask(taskId);
    setShowCustomTaskDialog(true);
  };

  const updateCustomTask = async () => {
    if (!editingCustomTask || !customTaskName.trim()) return;

    const newTasks = tasks.map(t =>
      t.id === editingCustomTask
        ? { ...t, description: customTaskName.trim(), note: customTaskNote.trim() || undefined }
        : t
    );
    setTasks(newTasks);
    await saveTasks(newTasks);

    // Reset dialog
    setCustomTaskName('');
    setCustomTaskNote('');
    setEditingCustomTask(null);
    setShowCustomTaskDialog(false);
  };

  const deleteCustomTask = async (taskId: string) => {
    const newTasks = tasks.filter(t => t.id !== taskId);
    setTasks(newTasks);
    await saveTasks(newTasks);
  };

  const savePreparations = async (updatedPreps: CablePreparation[]) => {
    setSaving(true);
    try {
      const checklist: Record<string, boolean> = {};
      // Include both preparations and tasks (to avoid overwriting tasks)
      updatedPreps.forEach(prep => {
        checklist[prep.id] = prep.completed;
      });
      tasks.forEach(task => {
        checklist[task.id] = task.completed;
      });

      console.log('[InstallationChecklist] Saving preparations:', checklist);
      // Use updatePole which calls the correct API endpoint (PUT)
      await updatePole(pole.project_id, pole.id, { installation_checklist: checklist });
    } catch (error) {
      console.error('Failed to save preparations:', error);
    } finally {
      setSaving(false);
    }
  };

  const saveTasks = async (updatedTasks: SpliceTask[]) => {
    setSaving(true);
    try {
      const checklist: Record<string, boolean> = {};
      const customTasks: Record<string, { description: string; note?: string; completed: boolean }> = {};

      // Include both preparations and tasks
      cablePreparations.forEach(prep => {
        checklist[prep.id] = prep.completed;
      });

      updatedTasks.forEach(task => {
        if (task.type === 'custom') {
          // Store custom tasks separately with full data
          customTasks[task.id] = {
            description: task.description,
            note: task.note,
            completed: task.completed,
          };
        } else {
          // Regular tasks just store completion status
          checklist[task.id] = task.completed;
        }
      });

      console.log('[InstallationChecklist] Saving tasks:', { checklist, customTasks });

      // Use updatePole which calls the correct API endpoint (PUT)
      await updatePole(pole.project_id, pole.id, {
        installation_checklist: checklist,
        custom_tasks: customTasks,
      });
    } catch (error) {
      console.error('Failed to save checklist:', error);
    } finally {
      setSaving(false);
    }
  };

  const proceedToSplicing = () => {
    const allPrepped = cablePreparations.every(p => p.completed);
    if (!allPrepped) {
      alert('Najprv priprav všetky káble!');
      return;
    }
    setCurrentStep('splicing');
  };

  const handleTouchStart = (e: React.TouchEvent, taskId: string) => {
    setTouchStart({ x: e.touches[0].clientX, taskId });
  };

  const handleTouchMove = (e: React.TouchEvent, taskId: string) => {
    if (!touchStart || touchStart.taskId !== taskId) return;
    const deltaX = e.touches[0].clientX - touchStart.x;
    if (deltaX > 0) {
      setSwipeOffset({ ...swipeOffset, [taskId]: Math.min(deltaX, 100) });
    }
  };

  const handleTouchEnd = (taskId: string) => {
    if (!touchStart || touchStart.taskId !== taskId) return;
    const offset = swipeOffset[taskId] || 0;
    if (offset > 50) {
      toggleTask(taskId);
    }
    setSwipeOffset({ ...swipeOffset, [taskId]: 0 });
    setTouchStart(null);
  };

  // Prípojka, z ktorej technik práve pripája zákazníka (splitter býva na inom
  // stĺpiku, než na ktorom stojí)
  const [pripajam, setPripajam] = useState<SpliceTask['dropTarget'] | null>(null);
  const dropTasks = tasks.filter(t => t.type === 'drop');

  // Only count tasks that can be completed (exclude disconnected and connected-to-splitter)
  const completableTasks = tasks.filter(t => {
    // Exclude disconnected ports
    if (!t.toCable && t.description.includes('Nepripojené')) {
      return false;
    }
    // Exclude ports connected to other splitters
    if (t.description.includes('→ Splitter #')) {
      return false;
    }
    return true;
  });

  const completedCount = completableTasks.filter(t => t.completed).length;
  const totalCount = completableTasks.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  // Group splices by cable pairs and splitters
  const { cableGroups, splitterGroups } = useMemo(() => {
    const groups: CableGroup[] = [];
    const splitterGrps: SplitterGroup[] = [];

    // Separate regular splices from splitter tasks
    const regularSplices = tasks.filter(t => t.type === 'splice');
    const splitterInputs = tasks.filter(t => t.type === 'splitter_input');
    const splitterOutputs = tasks.filter(t => t.type === 'splitter_output');

    // Group regular splices by cable pair
    const groupMap = new Map<string, SpliceTask[]>();

    regularSplices.forEach(task => {
      const fromId = task.fromCable?.id || 'olt';
      const toId = task.toCable?.id || 'unknown';
      const key = `${fromId}_${toId}`;

      if (!groupMap.has(key)) {
        groupMap.set(key, []);
      }
      groupMap.get(key)!.push(task);
    });

    // Convert map to array of CableGroup
    groupMap.forEach((splices, key) => {
      const firstTask = splices[0];
      const fromCableName = firstTask.fromCable
        ? getCableName(firstTask.fromCable)
        : `OLT Port ${firstTask.description.match(/OLT Port (\d+)/)?.[1] || '?'}`;
      const toCableName = firstTask.toCable
        ? getCableName(firstTask.toCable)
        : 'Unknown';

      groups.push({
        id: key,
        fromCable: firstTask.fromCable,
        toCable: firstTask.toCable,
        fromCableName,
        toCableName,
        splices: splices.sort((a, b) => (a.fromFiber || 0) - (b.fromFiber || 0)),
        allCompleted: splices.every(s => s.completed),
      });
    });

    // Group splitter tasks
    splitterInputs.forEach(inputTask => {
      // Find matching output tasks (same index)
      const outputs = splitterOutputs.filter(out => out.index === inputTask.index);

      // Extract splitter type from description (e.g., "1:4")
      const typeMatch = inputTask.description.match(/(\d+:\d+)/);
      const splitterType = typeMatch ? typeMatch[1] : '1:4';

      // Filter out disconnected and connected-to-splitter outputs for completion check
      const completableOutputs = outputs.filter(o => {
        // Exclude disconnected ports
        if (!o.toCable && o.description.includes('Nepripojené')) {
          return false;
        }
        // Exclude ports connected to other splitters
        if (o.description.includes('→ Splitter #')) {
          return false;
        }
        return true;
      });

      splitterGrps.push({
        id: `splitter_${inputTask.index}`,
        splitterType,
        inputTask,
        outputTasks: outputs.sort((a, b) => {
          // Sort by port number (use splitterPort if available, otherwise parse from description)
          const portA = a.splitterPort || parseInt(a.description.match(/port (\d+)/)?.[1] || '0');
          const portB = b.splitterPort || parseInt(b.description.match(/port (\d+)/)?.[1] || '0');
          return portA - portB;
        }),
        allCompleted: inputTask.completed && completableOutputs.every(o => o.completed),
      });
    });

    return { cableGroups: groups, splitterGroups: splitterGrps };
  }, [tasks]);

  const outputSignals = useMemo(() => {
    const signals: Array<{ description: string; signal: number | null; completed: boolean }> = [];
    tasks.forEach(task => {
      if (task.signalAtEnd !== null && task.signalAtEnd !== undefined) {
        signals.push({
          description: task.description,
          signal: task.signalAtEnd,
          completed: task.completed,
        });
      }
    });
    return signals;
  }, [tasks]);

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content installation-checklist-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>📋 Inštalačný Checklist - {currentStep === 'preparation' ? 'Príprava vlákien' : 'Zváranie'}</h2>
          <button className="dialog-close" onClick={onClose}>✕</button>
        </div>

        <div className="dialog-body">
          <div className="checklist-pole-info">
            <h3>{pole.name}</h3>
            <p>{pole.pole_type === 'olt' ? 'OLT' : 'Stĺpik'}</p>
          </div>

          {currentStep === 'preparation' ? (
            <>
              <div className="checklist-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${(cablePreparations.filter(p => p.completed).length / cablePreparations.length) * 100}%` }}></div>
                </div>
                <div className="progress-text">
                  {cablePreparations.filter(p => p.completed).length}/{cablePreparations.length} káblov pripravených
                </div>
              </div>

              <div className="checklist-preparations">
                {cablePreparations.map(prep => (
                  <div
                    key={prep.id}
                    className={`checklist-task ${prep.completed ? 'completed' : ''}`}
                    onClick={() => toggleCablePreparation(prep.id)}
                  >
                    <div className="task-checkbox">
                      {prep.completed ? '✅' : '⬜'}
                    </div>
                    <div className="task-content">
                      <div className="task-description">
                        <strong>Kábel:</strong> {getCableName(prep.cable)}
                      </div>
                      <div className="task-fibers">
                        <strong>{prep.fibers.length} vlákien:</strong> {prep.fiberColors.join(', ')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="checklist-actions">
                <button
                  className="btn-proceed"
                  onClick={proceedToSplicing}
                  disabled={!cablePreparations.every(p => p.completed)}
                >
                  Pokračovať na zváranie →
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="checklist-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
                <div className="progress-text">
                  {completedCount}/{totalCount} hotovo ({Math.round(progress)}%)
                </div>
              </div>

              <div className="checklist-tasks">
                {/* Prípojky k zákazníkom — vlákno, ktoré odtiaľto ide k domu.
                    Splitter, z ktorého prišlo, môže stáť na inom stĺpiku, takže
                    tu musí byť napísané kde a či je port voľný — a dať sa naň
                    rovno pripojiť zákazník. */}
                {dropTasks.length > 0 && (
                  <div className="cable-group" style={{ borderLeftColor: '#f59e0b' }}>
                    <div className="cable-group-header">
                      <span className="cable-group-icon">🏠</span>
                      <span className="cable-group-title">Prípojky k zákazníkom</span>
                      <span className="cable-group-progress">
                        {dropTasks.filter(t => t.completed).length}/{dropTasks.length}
                      </span>
                    </div>

                    {dropTasks.map(task => {
                      const st = task.toFiber ? getFiberColorStyle(task.toFiber) : null;
                      const volny = !!task.dropTarget && !task.dropTarget.zakaznik;
                      return (
                        <div key={task.id} className={`drop-row ${task.completed ? 'completed' : ''}`}>
                          <div className="drop-row-main" onClick={() => toggleTask(task.id)}>
                            <span className="drop-check">{task.completed ? '✅' : '⬜'}</span>
                            <span
                              className="drop-fiber"
                              style={st ? { backgroundColor: st.bg, color: st.text } : undefined}
                            >
                              {task.toFiber} {task.toFiber ? getFiberColorName(task.toFiber) : ''}
                            </span>
                            <span className="drop-text">{task.description}</span>
                            <span className="drop-signal">
                              {task.signalAtEnd !== null && task.signalAtEnd !== undefined
                                ? `${task.signalAtEnd.toFixed(1)} dBm`
                                : '—'}
                            </span>
                          </div>
                          {volny && (
                            <button
                              className="btn-drop-connect"
                              onClick={() => setPripajam(task.dropTarget!)}
                            >
                              ➕ Pripojiť zákazníka na {task.dropTarget!.poleName}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Cable Groups - Grouped Splice Tables */}
                {cableGroups.map(group => {
                  const cableColor = getCableGroupColor(group.fromCable?.id || group.toCable?.id);
                  return (
                    <div
                      key={group.id}
                      className={`cable-group ${group.allCompleted ? 'completed' : ''}`}
                      style={{ borderLeftColor: cableColor }}
                    >
                      <div className="cable-group-header">
                        <span className="cable-group-icon">📦</span>
                        <span className="cable-group-title">
                          {group.fromCableName} ⟷ {group.toCableName}
                        </span>
                        <span className="cable-group-progress">
                          {group.splices.filter(s => s.completed).length}/{group.splices.length}
                        </span>
                      </div>

                      <div className="splice-table">
                        <div className="splice-table-header">
                          <div className="splice-col-check">✓</div>
                          <div className="splice-col-from">Z vlákna</div>
                          <div className="splice-col-to">Do vlákna</div>
                          <div className="splice-col-signal">Signál</div>
                        </div>

                        {group.splices.map(splice => {
                          const fromFiberStyle = splice.fromFiber ? getFiberColorStyle(splice.fromFiber) : null;
                          const toFiberStyle = splice.toFiber ? getFiberColorStyle(splice.toFiber) : null;

                          return (
                            <div
                              key={splice.id}
                              className={`splice-table-row ${splice.completed ? 'completed' : ''}`}
                              onClick={() => toggleTask(splice.id)}
                            >
                              <div className="splice-col-check">
                                {splice.completed ? '✅' : '⬜'}
                              </div>
                              <div
                                className="splice-col-from"
                                style={fromFiberStyle ? {
                                  backgroundColor: fromFiberStyle.bg,
                                  color: fromFiberStyle.text,
                                  fontWeight: 600
                                } : undefined}
                              >
                                {splice.fromFiber ? `${splice.fromFiber} ${getFiberColorName(splice.fromFiber)}` : 'OLT'}
                              </div>
                              <div
                                className="splice-col-to"
                                style={toFiberStyle ? {
                                  backgroundColor: toFiberStyle.bg,
                                  color: toFiberStyle.text,
                                  fontWeight: 600
                                } : undefined}
                              >
                                {splice.toFiber ? `${splice.toFiber} ${getFiberColorName(splice.toFiber)}` : '-'}
                              </div>
                              <div className="splice-col-signal">
                                {splice.signalAtEnd !== null && splice.signalAtEnd !== undefined
                                  ? `${splice.signalAtEnd.toFixed(1)} dBm`
                                  : '-'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Splitter Groups - Grouped Splitter Tables */}
                {splitterGroups.map(group => {
                  const cableColor = getCableGroupColor(group.inputTask.fromCable?.id);
                  const inputFiberStyle = group.inputTask.fromFiber ? getFiberColorStyle(group.inputTask.fromFiber) : null;

                  // Filter completable outputs (exclude disconnected and connected-to-splitter)
                  const completableOutputs = group.outputTasks.filter(o => {
                    if (!o.toCable && o.description.includes('Nepripojené')) return false;
                    if (o.description.includes('→ Splitter #')) return false;
                    return true;
                  });

                  return (
                    <div
                      key={group.id}
                      className={`cable-group splitter-group ${group.allCompleted ? 'completed' : ''}`}
                      style={{ borderLeftColor: cableColor }}
                    >
                      <div className="cable-group-header">
                        <span className="cable-group-icon">🔀</span>
                        <span className="splitter-number">#{group.inputTask.index + 1}</span>
                        <span className="cable-group-title">
                          Splitter {group.splitterType} na stĺpiku {pole.name}
                        </span>
                        <span className="cable-group-progress">
                          {(group.inputTask.completed ? 1 : 0) + completableOutputs.filter(o => o.completed).length}/{1 + completableOutputs.length}
                        </span>
                      </div>

                      <div className="splitter-table">
                        {/* Input Row */}
                        <div className="splitter-input-row">
                          <div className="splitter-input-label">
                            <strong>Vstup:</strong>
                          </div>
                          <div
                            className="splitter-input-fiber"
                            style={inputFiberStyle ? {
                              backgroundColor: inputFiberStyle.bg,
                              color: inputFiberStyle.text,
                              fontWeight: 600
                            } : undefined}
                          >
                            {/* Parse input from description */}
                            {group.inputTask.description.includes('←')
                              ? group.inputTask.description.split('←')[1].trim()
                              : (group.inputTask.fromCable ? getCableName(group.inputTask.fromCable) : 'OLT')}
                          </div>
                          <div className="splitter-input-check" onClick={() => toggleTask(group.inputTask.id)}>
                            {group.inputTask.completed ? '✅' : '⬜'}
                          </div>
                        </div>

                        {/* Output Table */}
                        <div className="splitter-outputs">
                          <div className="splitter-output-header">
                            <div className="output-col-check">✓</div>
                            <div className="output-col-port">Port</div>
                            <div className="output-col-cable">Kábel</div>
                            <div className="output-col-fiber">Vlákno</div>
                            <div className="output-col-signal">Signál</div>
                          </div>

                          {group.outputTasks.map(output => {
                            const outputFiberStyle = output.toFiber ? getFiberColorStyle(output.toFiber) : null;
                            // Use splitterPort if available, otherwise parse from description
                            const portNum = output.splitterPort || parseInt(output.description.match(/port (\d+)/)?.[1] || '0') || '?';

                            // Check if disconnected or connected to splitter
                            const isDisconnected = !output.toCable && !output.description.includes('Splitter #');
                            const isConnectedToSplitter = output.description.includes('→ Splitter #');

                            // Extract target info from description
                            let targetInfo = '';
                            if (isConnectedToSplitter) {
                              const match = output.description.match(/→ (Splitter #\d+)/);
                              targetInfo = match ? match[1] : '';
                            } else if (isDisconnected) {
                              targetInfo = 'Nepripojené';
                            }

                            return (
                              <div
                                key={output.id}
                                className={`splitter-output-row ${output.completed ? 'completed' : ''} ${isDisconnected ? 'disconnected' : ''} ${isConnectedToSplitter ? 'connected-to-splitter' : ''}`}
                                onClick={() => (!isDisconnected && !isConnectedToSplitter) ? toggleTask(output.id) : undefined}
                                style={{ cursor: (!isDisconnected && !isConnectedToSplitter) ? 'pointer' : 'default' }}
                              >
                                <div className="output-col-check">
                                  {isDisconnected ? '⚪' : (isConnectedToSplitter ? '🔀' : (output.completed ? '✅' : '⬜'))}
                                </div>
                                <div className="output-col-port">
                                  {portNum}
                                </div>
                                <div className="output-col-cable">
                                  {isConnectedToSplitter ? (
                                    <strong style={{ color: '#9C27B0' }}>{targetInfo}</strong>
                                  ) : (
                                    output.toCable ? getCableName(output.toCable) : <em style={{ color: '#999' }}>{targetInfo}</em>
                                  )}
                                </div>
                                <div
                                  className="output-col-fiber"
                                  style={outputFiberStyle ? {
                                    backgroundColor: outputFiberStyle.bg,
                                    color: outputFiberStyle.text,
                                    fontWeight: 600
                                  } : undefined}
                                >
                                  {output.toFiber ? `${output.toFiber} ${getFiberColorName(output.toFiber)}` : '-'}
                                </div>
                                <div className="output-col-signal">
                                  {output.signalAtEnd !== null && output.signalAtEnd !== undefined
                                    ? `${output.signalAtEnd.toFixed(1)} dBm`
                                    : '-'}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Custom Tasks Section */}
              <div className="custom-tasks-section">
                <div className="custom-tasks-header">
                  <h4>📝 Vlastné úlohy</h4>
                  <button
                    className="btn-add-custom-task"
                    onClick={() => {
                      setCustomTaskName('');
                      setCustomTaskNote('');
                      setEditingCustomTask(null);
                      setShowCustomTaskDialog(true);
                    }}
                  >
                    + Pridať úlohu
                  </button>
                </div>

                {tasks.filter(t => t.type === 'custom').length > 0 && (
                  <div className="custom-tasks-list">
                    {tasks.filter(t => t.type === 'custom').map(task => (
                      <div key={task.id} className={`custom-task-item ${task.completed ? 'completed' : ''}`}>
                        <div className="custom-task-checkbox" onClick={() => toggleTask(task.id)}>
                          {task.completed ? '✅' : '⬜'}
                        </div>
                        <div className="custom-task-content">
                          <div className="custom-task-description">{task.description}</div>
                          {task.note && <div className="custom-task-note">{task.note}</div>}
                        </div>
                        <div className="custom-task-actions">
                          <button
                            className="btn-edit-custom-task"
                            onClick={() => editCustomTask(task.id)}
                            title="Upraviť"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-delete-custom-task"
                            onClick={() => {
                              if (window.confirm('Naozaj chcete zmazať túto úlohu?')) {
                                deleteCustomTask(task.id);
                              }
                            }}
                            title="Zmazať"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {outputSignals.length > 0 && (
                <div className="checklist-summary">
                  <h4>📊 Zhrnutie výstupných signálov</h4>
                  <div className="summary-signals">
                    {outputSignals.map((sig, idx) => (
                      <div key={idx} className={`summary-signal ${sig.completed ? 'completed' : 'pending'}`}>
                        <span className="signal-icon">{sig.completed ? '✅' : '⬜'}</span>
                        <span className="signal-desc">{sig.description}</span>
                        <span className="signal-value">
                          {sig.signal !== null ? `${sig.signal.toFixed(1)} dBm` : 'N/A'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="dialog-footer">
          {currentStep === 'splicing' && (
            <button className="btn-secondary" onClick={() => setCurrentStep('preparation')}>
              ← Späť na prípravu
            </button>
          )}
          <button className="btn-secondary" onClick={onClose}>Zavrieť</button>
          {saving && <span className="saving-indicator">💾 Ukladám...</span>}
        </div>
      </div>

      {/* Custom Task Dialog */}
      {showCustomTaskDialog && (
        <div className="dialog-overlay" onClick={() => setShowCustomTaskDialog(false)}>
          <div className="dialog-content custom-task-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>{editingCustomTask ? '✏️ Upraviť úlohu' : '➕ Pridať vlastnú úlohu'}</h3>
              <button className="close-button" onClick={() => setShowCustomTaskDialog(false)}>×</button>
            </div>

            <div className="dialog-body">
              <div className="form-group">
                <label>Názov úlohy *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Napr. Zmeraj signal, Skontroluj kazetu..."
                  value={customTaskName}
                  onChange={(e) => setCustomTaskName(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Poznámka (voliteľné)</label>
                <textarea
                  className="form-textarea"
                  placeholder="Napr. Výsledok merania, poznámky..."
                  value={customTaskNote}
                  onChange={(e) => setCustomTaskNote(e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <div className="dialog-footer">
              <button className="btn-secondary" onClick={() => setShowCustomTaskDialog(false)}>
                Zrušiť
              </button>
              <button
                className="btn-primary"
                onClick={editingCustomTask ? updateCustomTask : addCustomTask}
                disabled={!customTaskName.trim()}
              >
                {editingCustomTask ? 'Uložiť' : 'Pridať'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pripojenie zákazníka rovno z prípojky — montážny dialóg otvorený nad
          stĺpikom, kde splitter naozaj stojí, s predvybraným portom. */}
      {pripajam && (() => {
        const cielovyStlp = poles.find(p => p.id === pripajam.poleId);
        if (!cielovyStlp) return null;
        return (
          <InstallationTechDialog
            pole={cielovyStlp}
            projectId={pole.project_id}
            preselect={{
              poleId: pripajam.poleId,
              splitterIndex: pripajam.splitterIndex,
              outputNumber: pripajam.outputNumber,
              popis: pripajam.popis,
            }}
            onClose={() => setPripajam(null)}
            onCustomerConnected={async () => {
              setPripajam(null);
              await fetchPoles(pole.project_id);
            }}
          />
        );
      })()}
    </div>
  );
};
